/** @file useJointEdit: a node key at the playhead and the edit path Joint and Transform share. */
import type { Clip, NodeKey } from '@zilo/pose3d-format/types/clip';
import type { PoseOverride } from '../../state/editorTypes';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { mirrorStart } from '../timeline/mirrorStart';
import { commitPoseOverride } from './commitPoseOverride';
import { jointRotation, type JointRotation } from './jointRotation';

/**
 * The clip on the playhead, the shown rotation, whether the frame is in a mirrorHalf clip's computed half, the
 * pending override, and the edits: `preview` shows the node's key at this frame with `fields` replaced (through
 * `poseOverride`), `commit` sends the pending override as one command, `cancel` drops it (Esc during a scrub), `setRot` previews one of yaw (0), pitch (1),
 * roll (2) at `deg`, or commits when `final`.
 */
export type JointEdit = {
  clipName: string; clip: Clip; frame: number; joint: JointRotation; locked: boolean; override: PoseOverride | null;
  preview: (fields: NodeKey) => void;
  commit: () => void;
  cancel: () => void;
  setRot: (axis: number, deg: number, final: boolean) => void;
};

/**
 * Null without a clip on the playhead. Every edit goes through `poseOverride` and `commitPoseOverride` (one
 * `clip.setPose` on release), so the Joint sliders and the Transform rows write the same command for the same value.
 */
export function useJointEdit(node: string): JointEdit | null {
  const store = useEditorStore();
  const clipName = useEditor((s) => s.playhead.clip);
  const frame = useEditor((s) => s.playhead.frame);
  const clip = useEditor((s) => (clipName === null ? undefined : s.puppet.doc?.clips[clipName]));
  const rest = useEditor((s) => s.puppet.doc?.puppet.nodes[node]?.rest);
  const override = useEditor((s) => s.poseOverride);
  if (clipName === null || clip === undefined) return null;
  const joint = jointRotation(clipName, clip, frame, node, override, rest);
  const start = mirrorStart(clip);
  const locked = start !== null && frame >= start;
  const preview = (fields: NodeKey): void => {
    if (locked) return;
    store.getState().setPoseOverride({ clip: clipName, frame, nodes: { [node]: { ...(joint.exact ?? {}), ...fields } } });
  };
  const commit = (): void => { commitPoseOverride(store); };
  const cancel = (): void => { store.getState().setPoseOverride(null); };
  const setRot = (axis: number, deg: number, final: boolean): void => {
    if (final) { commit(); return; }
    const rot: [number, number, number] = [...joint.rot];
    rot[axis] = deg;
    preview({ rot });
  };
  return { clipName, clip, frame, joint, locked, override, preview, commit, cancel, setRot };
}
